import type { HeaderLogoVariant, HeaderProps } from './types';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import type { Surfaces } from '@dx-ui/global-ui-prism';
import type { FC } from 'react';
import { darkSurfaces } from './header.utilities';

/** Maps a Prism surface to the HeaderLogo image variant that stays legible on it. */
export const getLogoVariant = (surfaceType?: Surfaces): HeaderLogoVariant =>
  surfaceType && darkSurfaces.includes(surfaceType) ? 'inverted' : 'default';

export const Logo: FC<{
  variant?: HeaderLogoVariant;
  brand: HeaderProps['brand'];
}> = ({ variant = 'default', brand }) => {
  const { t } = useTranslation('osc-header');

  const altText = (() => {
    switch (brand.code) {
      case 'WW':
        return t('hiltonForTheStay');
      case 'OU':
      case 'EE':
        return `${t('hiltonForTheStay')}, ${brand.name}`;
      case 'EJ':
        return `${t('hiltonHonors')}, ${brand.name}`;
      case 'LX':
        return `${t('hiltonForTheStay')}, SLH`;
      case 'YH':
        return `${brand.name}, Select by Hilton`;
      default:
        return brand.name;
    }
  })();

  return (
    <img
      src={
        variant === 'default'
          ? `/modules/assets/svgs/logos/${brand.code}.svg`
          : `/modules/assets/svgs/logos/${brand.code}_${variant}.svg`
      }
      //Logo for worldwide has marketing text. So only when WW is passed use custom alt text override
      alt={altText}
      className="header-logo"
    />
  );
};
// TODO: Refactor to use context when pods fully adopt new header
export const HeaderLogoLink: FC<{
  className?: string;
  anchorClassName?: string;
  /** @deprecated Pass `variant` instead — kept for existing callers. */
  surfaceType?: Surfaces;
  variant?: HeaderLogoVariant;
  brand: HeaderProps['brand'];
  heading?: string;
}> = ({ className, surfaceType, variant, heading, anchorClassName, brand }) => {
  const { t } = useTranslation('osc-header');

  const Wrapper = heading ? 'h1' : 'span';

  return (
    <Wrapper aria-label={heading ? heading : undefined} className={className}>
      <a
        data-testid="brand-logo-link"
        href={brand.url}
        className={cx(
          'relative block h-14 w-28 overflow-hidden brand-ee:lg:w-44 brand-ej:lg:w-44 brand-lx:lg:w-44 brand-ou:lg:w-72 brand-yh:lg:w-72',
          anchorClassName
        )}
        {...(!!brand.onClick && { onClick: brand.onClick })}
        {...(!!brand.target && { target: brand.target })}
        {...(brand?.target === '_blank' && { rel: 'noreferrer' })}
      >
        <Logo variant={variant ?? getLogoVariant(surfaceType)} brand={brand} />
        <span className="sr-only">{brand?.target === '_blank' ? `, ${t('newTab')}` : ''}</span>
      </a>
    </Wrapper>
  );
};
